import type { InitOptions } from 'i18next';
import type { Theme, Components } from '@mui/material/styles';

// MUI Core Locales
import { itIT as itITCore, enUS as enUSCore } from '@mui/material/locale';
// MUI Date Pickers Locales
import { enUS as enUSDate, itIT as itITDate } from '@mui/x-date-pickers/locales';
// MUI Data Grid Locales
import { enUS as enUSDataGrid, itIT as itITDataGrid } from '@mui/x-data-grid/locales';

// ----------------------------------------------------------------------
// Import translation files directly
import enTranslations from './langs/en/en.json';
import itTranslations from './langs/it/it.json';

// Supported languages
export const supportedLngs = ['en', 'it'] as const;
export type LangCode = (typeof supportedLngs)[number];

// Fallback and default namespace
export const fallbackLng: LangCode = 'it';

// Storage config
export const storageConfig = {
  cookie: { key: 'i18next', autoDetection: false },
  localStorage: { key: 'i18nextLng', autoDetection: false },
} as const;

// ----------------------------------------------------------------------

/**
 * @countryCode https://flagcdn.com/en/codes.json
 * @adapterLocale https://github.com/iamkun/dayjs/tree/master/src/locale
 * @numberFormat https://simplelocalize.io/data/locales/
 */

export type LangOption = {
  value: LangCode;
  label: string;
  countryCode: string;
  adapterLocale?: string;
  numberFormat: { code: string; currency: string };
  systemValue?: { components: Components<Theme> };
};

export const allLangs: LangOption[] = [
  {
    value: 'en',
    label: 'English',
    countryCode: 'GB',
    adapterLocale: 'en',
    numberFormat: { code: 'en-US', currency: 'EUR' },
    systemValue: {
      components: { ...enUSCore.components, ...enUSDate.components, ...enUSDataGrid.components },
    },
  },
  {
    value: 'it',
    label: 'Italian',
    countryCode: 'IT',
    adapterLocale: 'it',
    numberFormat: { code: 'en-IT', currency: 'EUR' },
    systemValue: {
      components: { ...itITCore.components, ...itITDate.components, ...itITDataGrid.components },
    },
  },
];
export const defaultLang = allLangs.find((lang) => lang.value === fallbackLng)!;

// ----------------------------------------------------------------------

// Create resources object for i18next - flat namespace structure
const createResources = (translations: Record<string, unknown>) => {
  const resources: Record<string, any> = {};

  // Add common translations
  if (translations.common) {
    resources.common = translations.common;
  }

  // Dynamically add all nested sections as flat namespaces
  Object.entries(translations).forEach(([sectionKey, sectionValue]) => {
    if (sectionKey !== 'common' && typeof sectionValue === 'object' && sectionValue !== null) {
      Object.entries(sectionValue).forEach(([pageKey, pageValue]) => {
        resources[pageKey] = pageValue;
      });
    }
  });

  return resources;
};

const resources = {
  en: createResources(enTranslations),
  it: createResources(itTranslations),
} as const;

// Dynamically generate namespace list
const generateNamespaces = (): string[] => {
  const namespaces = new Set<string>();

  // Extract all page keys from all language resources to ensure completeness
  Object.values(resources).forEach((langResources) => {
    Object.keys(langResources).forEach((key) => {
      namespaces.add(key);
    });
  });

  return Array.from(namespaces);
};

export function i18nOptions(lang = fallbackLng): InitOptions {
  const generatedNamespaces = generateNamespaces();
  return {
    // debug: true,
    supportedLngs,
    fallbackLng,
    lng: lang,
    resources,
    defaultNS: 'common', // Use 'common' as deterministic default namespace
    ns: generatedNamespaces, // Dynamically generated
    interpolation: {
      escapeValue: false, // React already escapes
    },
  };
}

export function getCurrentLang(lang?: string): LangOption {
  const fallbackLang = allLangs.find((l) => l.value === fallbackLng) ?? allLangs[0];

  if (!lang) {
    return fallbackLang;
  }

  return allLangs.find((l) => l.value === lang) ?? fallbackLang;
}
